/**
 * Plain functions behind `PlanMapHost`'s 3D fixes (`spec-onboarding-3d-plan-plan-host-fixes`, R3 and
 * R4), kept apart from React and WebGL so they can be tested on real three.js objects and a store
 * of R3F's shape.
 *
 * ## Why the frameloop is held at "never"
 *
 * The host's react-three-map `Canvas` is an overlay: react-three-map renders R3F's own `Canvas` on a
 * canvas of its own and passes the `frameloop` prop straight through. Left at `"demand"`, R3F runs
 * its own loop, and every `invalidate()` (a prop change in the 3D tree, a model arriving, a resize)
 * draws a frame on R3F's own animation frame, outside the map's render, with the camera of the last
 * map frame. On its in-layer path react-three-map replaces the store's `invalidate` with one that
 * asks the map to repaint (and stays at `"demand"`). `holdFrameloop` does that for the overlay and
 * also holds `"never"`: R3F draws only when a map renders (react-three-map's layer calls `advance`),
 * and every invalidation asks the map for that render, so a change is still drawn.
 *
 * One limit is react-three-map's, not the guard's: its layer calls R3F's `advance` with no root, so
 * any react-three-map map's render draws every R3F root on the page, each with its own last camera.
 * With two plan maps mounted, a pan on one also redraws the other's overlay.
 */
import type { Object3D } from 'three';

type Frameloop = 'always' | 'demand' | 'never';

/** Every route to a map a hold has installed, with the R3F `invalidate` it replaced. */
const ROUTED = new WeakMap<(...args: never[]) => void, (...args: never[]) => void>();

/** The parts of R3F's root store (its public `useStore`) the guard reads and writes. */
export interface FrameloopStore {
  getState(): {
    frameloop: Frameloop;
    invalidate: (...args: never[]) => void;
    setFrameloop: (frameloop: Frameloop) => void;
    /** R3F's frame count still owed (its `RootState.internal`, typed by R3F). */
    internal?: { frames: number };
    clock?: { elapsedTime: number };
  };
  setState(partial: { invalidate: (...args: never[]) => void }): void;
  subscribe(listener: () => void): () => void;
}

/**
 * Holds R3F at `"never"` and routes its `invalidate` to the map, until the returned function is
 * called. Any move off `"never"` (R3F's `configure` on a re-render of its `Canvas`) is put back in
 * the same store update: R3F's store notifies its listeners synchronously inside `setState`, before
 * `setFrameloop` returns and before R3F could schedule a frame. A frame R3F already owes is dropped
 * too: its loop draws one whatever the frameloop says (`internal.frames > 0`, R3F 9's own
 * bookkeeping, so an R3F upgrade re-checks this). And because `setFrameloop('never')` zeroes R3F's
 * clock while react-three-map advances it with `Date.now()` seconds, the clock is set to now, so the
 * next frame's `delta` is one frame, not the whole epoch.
 */
export const holdFrameloop = (store: FrameloopStore, map: { triggerRepaint(): void }) => {
  const current = store.getState().invalidate;
  // R3F's own, even when another hold already routes it (StrictMode can mount a guard twice).
  const own = ROUTED.get(current) ?? current;
  // Read at call time: react-three-map swaps the map's `triggerRepaint` while its layer draws, to
  // hold a request made mid-frame until the frame ends.
  const toMap = () => map.triggerRepaint();
  ROUTED.set(toMap, own);
  const hold = () => {
    const state = store.getState();
    if (state.frameloop !== 'never') {
      state.setFrameloop('never');
      if (state.clock) state.clock.elapsedTime = Date.now() / 1000;
    }
    if (state.internal && state.internal.frames > 0) state.internal.frames = 0;
    // Any hold's route will do, so two holds never take it from each other.
    if (!ROUTED.has(store.getState().invalidate)) store.setState({ invalidate: toMap });
  };
  hold();
  const unsubscribe = store.subscribe(hold);
  return () => {
    unsubscribe();
    if (store.getState().invalidate === toMap) store.setState({ invalidate: own });
  };
};

/**
 * The same hold, taken once from R3F's `onCreated` (its root's first layout effect, before R3F's
 * loop can draw anything): R3F's state there has `get` and `set` but no `subscribe`, so this hold
 * does not follow later changes. The guard inside the canvas takes the lasting one.
 */
export const holdFrameloopAtCreation = (
  state: { get(): unknown; set(partial: object): void },
  map: { triggerRepaint(): void },
) => {
  holdFrameloop(
    {
      getState: state.get as FrameloopStore['getState'],
      setState: state.set,
      subscribe: () => () => {},
    },
    map,
  );
};

export interface HostContentStore<T> {
  get(): T;
  set(next: T): void;
  subscribe(listener: () => void): () => void;
}

/** The latest value, for a subtree inside the 3D canvas to read with `useSyncExternalStore`. */
export const createHostContentStore = <T>(initial: T): HostContentStore<T> => {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (next: T) => {
      if (Object.is(next, value)) return;
      value = next;
      listeners.forEach(listener => listener());
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export interface Disposable {
  dispose(): void;
}

/**
 * What a host's content has drawn, held weakly: a resource the content dropped (and usually disposed
 * itself) can still be collected while the host stays mounted. What is still alive at unmount is
 * what something else holds (drei's model cache, troika's glyph atlas, the cached model geometry), which
 * is what keeps a closed map's renderer.
 */
export interface DrawnResources {
  seen: WeakSet<object>;
  refs: WeakRef<Disposable>[];
  walks: number;
}

export const createDrawnResources = (): DrawnResources => ({
  seen: new WeakSet(),
  refs: [],
  walks: 0,
});

/** Walks between prunes of the references whose resource has been collected. */
const PRUNE_EVERY = 600;

const isDisposable = (v: unknown): v is Disposable =>
  !!v && typeof (v as Disposable).dispose === 'function';

const isTexture = (v: unknown): v is Disposable =>
  !!v && (v as { isTexture?: boolean }).isTexture === true && isDisposable(v);

const add = (into: DrawnResources, r: Disposable) => {
  if (into.seen.has(r)) return;
  into.seen.add(r);
  into.refs.push(new WeakRef(r));
};

/**
 * Adds every geometry, material and material texture under `root` to `into`: a material's own
 * texture slots, and the textures in its `uniforms` (troika's text keeps its glyph atlas there).
 * Objects with neither geometry nor material add nothing; a material array is walked element by
 * element. A material shared by many meshes has its slots read once per walk, and again on every
 * walk: a texture can arrive in a material without its `version` moving (troika sets its shared
 * glyph atlas into its material's `uniforms` once the font has loaded).
 */
export const collectDrawnResources = (root: Object3D, into: DrawnResources) => {
  into.walks += 1;
  if (into.walks % PRUNE_EVERY === 0) into.refs = into.refs.filter(ref => ref.deref());
  const read = new Set<object>();
  root.traverse(o => {
    const { geometry, material } = o as Object3D & { geometry?: unknown; material?: unknown };
    if (isDisposable(geometry)) add(into, geometry);
    const materials = Array.isArray(material) ? material : material ? [material] : [];
    materials.forEach(m => {
      if (!isDisposable(m)) return;
      add(into, m);
      if (read.has(m)) return;
      read.add(m);
      const fields = m as unknown as Record<string, unknown>;
      Object.keys(fields).forEach(key => {
        if (isTexture(fields[key])) add(into, fields[key] as Disposable);
      });
      const uniforms = fields.uniforms as Record<string, { value?: unknown }> | undefined;
      if (uniforms && typeof uniforms === 'object') {
        Object.keys(uniforms).forEach(key => {
          const value = uniforms[key]?.value;
          if (isTexture(value)) add(into, value);
        });
      }
    });
  });
};

/**
 * Calls `dispose()` on each resource still alive and forgets them all. three.js answers `dispose` in
 * every renderer that uploaded the resource: each frees its GPU copy and removes its own listener, so
 * no removed renderer is kept. A renderer still drawing the resource uploads it again on its next
 * frame. Returns how many threw, and the first error's message; the rest are released regardless.
 */
export const releaseResources = (resources: DrawnResources) => {
  let failed = 0;
  let firstError: string | undefined;
  resources.refs.forEach(ref => {
    const r = ref.deref();
    if (!r) return;
    try {
      r.dispose();
    } catch (error) {
      failed += 1;
      if (firstError === undefined) {
        const kind = (r as { type?: unknown }).type;
        firstError = `${typeof kind === 'string' ? kind : 'resource'}: ${
          error instanceof Error ? error.message : String(error)
        }`;
      }
    }
  });
  resources.refs = [];
  resources.seen = new WeakSet();
  return { failed, firstError };
};
